/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @angular-eslint/no-host-metadata-property */
/* eslint-disable quote-props */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterContentInit, Component, ContentChild, DoCheck, Input, Optional } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm, UntypedFormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LcsChipGrid } from '../chip-grid/chip-grid';
import { LcsChipInput } from '../chip-input/chip-input';
import { LcsChipInputDirective } from '../chip-input/chip-input.directive';
import { LCS_SINGLE_SELECT_OPTIONS, LcsSingleSelect } from '../entity-select/single-select/single-select-base';
import { LcsSingleSelectDirective } from '../entity-select/single-select/single-select.directive';
import { createMultiSelectError } from './multi-select-errors';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    host: {
        '(focusout)': 'onFocusOut()',
        'class': 'lcs-chip-multi-select',
    },
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: LcsChipMultiSelectComponent,
        },
    ],
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'lcs-chip-multi-select',
    templateUrl: './multi-select.component.html',
    standalone: false
})
export class LcsChipMultiSelectComponent<TItem>
   implements AfterContentInit, DoCheck, ControlValueAccessor, MatFormFieldControl<TItem[]>
{
   @Input()
   set value(value: TItem[] | null) {
      this._value = value;
   }

   get value(): TItem[] | null {
      return this._value;
   }

   private _value: TItem[] | null;

   @Input()
   get disabled(): boolean {
      if (this.ngControl && this.ngControl.disabled != null) {
         return this.ngControl.disabled;
      }
      return this._disabled;
   }

   set disabled(value: boolean) {
      this._disabled = coerceBooleanProperty(value);
      this.disableChildren(this._disabled);
   }

   private _disabled = false;

   @Input()
   set required(value: boolean) {
      this._required = coerceBooleanProperty(value);
   }

   get required(): boolean {
      return this._required;
   }

   private _required = false;

   @Input()
   get placeholder(): string {
      return this.chipInput?.placeholder;
   }

   @ContentChild(LcsChipInputDirective) contentChipInput: LcsChipInput<TItem>;

   @ContentChild(LCS_SINGLE_SELECT_OPTIONS) contentSingleSelect: LcsSingleSelect<TItem>;

   @ContentChild(LcsSingleSelectDirective) singleSelect: LcsSingleSelectDirective<TItem>;

   /** Whether any chips or the chip input has focus. */
   get focused(): boolean {
      return (this.chipInput?.focused || this.chipGrid?.focused) ?? false;
   }

   get id(): string {
      return this.chipInput.id;
   }

   get chipInput(): LcsChipInput<TItem> {
      return this._chipInput || this.contentChipInput;
   }

   get singleSelectOptions(): LcsSingleSelect<TItem> {
      return this._singleSelect || this.contentSingleSelect;
   }

   autofilled = false;

   touched = false;

   controlType = 'lcs-multi-select';

   get empty(): boolean {
      return (!this.chipInput || this.chipInput.empty) && this.value?.length === 0;
   }

   errorState: boolean;

   shouldLabelFloat = true;

   stateChanges = new Subject<void>();

   chipGrid: LcsChipGrid<TItem>;

   private _chipInput: LcsChipInput<TItem>;

   private _singleSelect: LcsSingleSelect<TItem>;

   ariaDescribedby: string | null = null;

   onChange: (value: TItem[] | null) => void;

   onTouched: () => void;

   constructor(
      public ngControl: NgControl,
      private defaultErrorStateMatcher: ErrorStateMatcher,
      @Optional() private parentFormGroup: FormGroupDirective,
      @Optional() private parentForm: NgForm
   ) {
      ngControl.valueAccessor = this;
   }

   ngDoCheck() {
      if (this.ngControl) {
         // We need to re-evaluate this on every change detection cycle, because there are some
         // error triggers that we can't subscribe to (e.g. parent form submissions). This means
         // that whatever logic is in here has to be super lean or we risk destroying the performance.
         this.updateErrorState();
      }
   }

   setDescribedByIds(ids: string[]): void {
      this.ariaDescribedby = ids.join(' ');
   }

   onContainerClick(event: MouseEvent): void {
      if (!this.chipGrid.originatesFromChip(event) && !this.chipInput.focused) {
         if (this.value && this.value.length > 0) {
            // focus a chip if we have one
            this.chipGrid.focus();
         } else {
            this.chipInput.focus();
         }
      } else {
         this.singleSelect.onContainerClick();
      }
   }

   registerChipGrid(chipGrid: LcsChipGrid<TItem>): void {
      this.chipGrid = chipGrid;
   }

   registerChipInput(chipInput: LcsChipInput<TItem>): void {
      this._chipInput = chipInput;
   }

   registerSingleSelect(singleSelect: LcsSingleSelect<TItem>): void {
      this._singleSelect = singleSelect;
   }

   writeValue(value: TItem[] | null): void {
      this.value = value;
      this.chipGrid.value = value;
      if (this.singleSelect) {
         this.singleSelect.updateExistingItems(this.chipGrid.value);
      }
      this.updateExcludedIds(value);
   }

   registerOnChange(fn: (value: TItem[] | null) => void): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
   }

   ngAfterContentInit(): void {
      if (!this.chipGrid) {
         throw createMultiSelectError('LcsChipGrid', 'registerChipGrid');
      }

      if (!this.chipInput) {
         throw createMultiSelectError('LcsChipInput', 'registerChipInput');
      }

      if (!this.singleSelectOptions) {
         throw createMultiSelectError('LcsSingleSelect', 'registerSingleSelect');
      }

      // we have to update the existing items on the single select here in case writeValue was called before content init
      this.singleSelect.updateExistingItems(this.chipGrid.value);
      this.updateExcludedIds(this.chipGrid.value);

      this.chipInput.registerChipGrid(this.chipGrid);

      this.singleSelect.valueChange
         .pipe(
            filter((value): value is TItem => {
               return value != null;
            })
         )
         .subscribe((value: TItem) => {
            this.chipGrid.add(value);
            this.singleSelect.value = null;
            this.singleSelect.updateExistingItems(this.chipGrid.value);
            this.onChange(this.chipGrid.value);
            this.updateExcludedIds(this.chipGrid.value);
         });

      this.chipGrid.remove.subscribe((_value: TItem) => {
         if (_value !== null) {
            this.singleSelect.removedExitsingItem(_value);
         }
         this.singleSelect.updateExistingItems(this.chipGrid.value);

         this.onChange(this.chipGrid.value);
         this.updateExcludedIds(this.chipGrid.value);
         if (this.chipGrid.value == null || (this.chipGrid.value.length === 0 && !this.chipInput.focused)) {
            this.chipInput.focus();
         }
      });
   }

   onFocusOut() {
      this.onTouched();
   }

   private updateErrorState() {
      const oldState = this.errorState;
      const parent = this.parentFormGroup || this.parentForm;
      const matcher = this.defaultErrorStateMatcher;
      const control = this.ngControl ? (this.ngControl.control as UntypedFormControl) : null;

      const newState = matcher.isErrorState(control, parent);
      if (newState !== oldState) {
         this.errorState = newState;
         this.stateChanges.next();
      }
   }

   private disableChildren(disabled: boolean) {
      setTimeout(() => {
         this.chipGrid.disabled = disabled;
         this.chipInput.disabled = disabled;
      });
   }

   private updateExcludedIds(value: TItem[] | null) {
      if (!this.contentSingleSelect) {
         return;
      }
      if (value) {
         const excludedIds: number[] = [];
         value.forEach((v) => {
            const id = this.contentSingleSelect.trackBy(v);
            if (id != null) {
               excludedIds.push(id);
            }
         });
         this.contentSingleSelect.excludedIds = excludedIds;
      } else {
         this.contentSingleSelect.excludedIds = [];
      }
   }
}
